import * as React from 'react';
import { ElementType, Ref } from 'react';
import { ResponsiveValue, variant } from 'styled-system';
import { createComponent, createHook } from 'reakit-system';
import { css, cx } from '@emotion/css';
import { forwardRefWithAs, PropsWithAs } from '../as';
import { TextHTMLProps, TextOptions, useText } from '../Text/Text';

type Level = ResponsiveValue<
  1 | 2 | 3 | 4 | 5 | 6 | '1' | '2' | '3' | '4' | '5' | '6'
>;

const level = variant({
  prop: 'level',
  variants: {
    1: {
      fontSize: ['fs-26', 'fs-32'],
    },
    2: {
      fontSize: ['fs-24', 'fs-26'],
    },
    3: {
      fontSize: ['fs-21', 'fs-22'],
    },
    4: {
      fontSize: ['fs-18'],
    },
    5: {
      fontSize: ['fs-16'],
    },
    6: {
      fontSize: ['fs-15'],
    },
  },
});
/**
 * @beta
 */
export type HeadingOptions = { level: Level } & Omit<
  TextOptions,
  'fontWeight' | 'lineHeight' | 'fontSize'
>;

export type HeadingHTMLProps = TextHTMLProps;

export type HeadingProps = HeadingHTMLProps & HeadingOptions;

export const useHeading = createHook<HeadingOptions, HeadingHTMLProps>({
  name: 'Heading',
  keys: ['level'],
  compose: [useText],
  useOptions(options) {
    return {
      ...options,
      lineHeight: 'lh-12',
      fontWeight: Number(options.level) === 1 ? 'bold' : 'semibold',
      styles: {
        ...options.styles,
        marginTop: '1.25em',
        marginBottom: '1em',
        '&:first-child': {
          marginTop: 0,
        },
        '&:last-child': {
          marginBottom: 0,
        },
      },
    };
  },
  useProps(options, props) {
    return {
      ...props,
      // @ts-expect-error
      className: cx(props.className, css({ label: 'Heading' }, level(options))),
    };
  },
});

const HeadingComponent = createComponent({
  as: 'h1',
  useHook: useHeading,
  memo: true,
});

/**
 * @beta
 */
// eslint-disable-next-line prefer-arrow-callback
export const Heading = forwardRefWithAs<HeadingProps, 'h1'>(function (
  { as, level = 1, ...props }: PropsWithAs<HeadingProps>,
  ref: Ref<HTMLHeadingElement>
) {
  const Component = as || `h${level}`;
  return (
    <HeadingComponent
      as={Component as ElementType}
      level={level}
      ref={ref}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
});
